import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Container from '@mui/material/Container';
import FormHelperText from '@mui/material/FormHelperText';
import { defaultTheme } from 'themes';
import { Button } from 'components/buttons';
import { useForm, useTranslation } from 'hooks';
import { FormContainer, FormItem, FormRow } from 'components/styledComponents';
import { TextField } from 'components/inputs';
import {
  Paper,
  Typo,
  NavLinkActive,
  LockLinkActive,
  InputWrapper,
  ErrorWrapper,
  Bold,
  FormFooter,
  BackLink,
  Warning,
  Text,
  LockedContainer
} from './style';

function LoginPage({ syncError, syncing, onLogin }) {
  const { t } = useTranslation();
  const { handleSubmit, useInput } = useForm({
    onSubmit: onLogin,
    validationSchema: 'loginSchema'
  });

  const username = useInput('username');
  const password = useInput('password');

  const translatedError = useMemo(() => syncError && t(`error.http.${syncError.status}01`), [
    syncError,
    t
  ]);

  useEffect(() => {
    localStorage.setItem('regUploadedFileName', '');
    return () => {
      localStorage.setItem('regUploadedFileName', '');
    };
  }, []);

  const handlePwdKeyUp = useCallback(
    (event) => {
      if (event.keyCode === 13) handleSubmit(event);
    },
    [handleSubmit]
  );

  const pwdInputProps = useMemo(() => ({ onKeyUp: handlePwdKeyUp }), [handlePwdKeyUp]);

  return (
    <Container maxWidth="xs">
      <Paper>
        {syncError?.payload?.locked ? (
          <LockedContainer>
            <Typo>
              <LockLinkActive>{t('label.accountLocked')}</LockLinkActive>
            </Typo>
            <Text first>{t('message.lockedMsg1')}</Text>
            <Text>{t('message.lockedMsg2')}</Text>
            <Text dangerouslySetInnerHTML={{ __html: t('message.lockedMsg3') }} />
          </LockedContainer>
        ) : (
          <>
            <Typo>
              <NavLinkActive>{t('title.loginTitle')}</NavLinkActive>
            </Typo>

            <FormContainer>
              <FormRow>
                <FormItem component={InputWrapper}>
                  <TextField
                    placeholder={t('label.emailAddress')}
                    required
                    fullWidth
                    {...username}
                  />
                </FormItem>
              </FormRow>

              <FormRow>
                <FormItem component={InputWrapper}>
                  <TextField
                    placeholder={t('label.password')}
                    required
                    type="password"
                    inputProps={pwdInputProps}
                    fullWidth
                    {...password}
                  />
                </FormItem>
              </FormRow>

              <ErrorWrapper>
                {translatedError && <FormHelperText error>{translatedError}</FormHelperText>}
              </ErrorWrapper>

              {syncError?.payload?.locked === false && syncError?.payload?.left_attempts > 0 && (
                <Warning>
                  <FormHelperText component={Bold} error>
                    {t('label.warning')}
                  </FormHelperText>
                  <FormHelperText error>{t('message.warningMsg')}</FormHelperText>
                </Warning>
              )}

              <FormFooter>
                <BackLink color={defaultTheme.color.primary.contrast} to="/forgot-password">
                  {t('link.forgotPassword')}
                </BackLink>
                <Button
                  color="primary"
                  disabled={syncing}
                  label={t('button.login')}
                  variant="lightContained"
                  onClick={handleSubmit}
                />
              </FormFooter>
            </FormContainer>
          </>
        )}
      </Paper>
    </Container>
  );
}

LoginPage.propTypes = {
  syncError: PropTypes.object,
  syncing: PropTypes.bool.isRequired,
  onLogin: PropTypes.func.isRequired
};

export default LoginPage;
